import axios from 'axios';
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Notice } from '@routes-plan/ui';
import { useT } from '../../../i18n';

/** Cupos de usuarios activos de la empresa (`GET users/seats`). Sin límite en las empresas que pagan por uso. */
export interface SeatState {
  limited: boolean;
  seats: number | null;
  used: number;
  available: number | null;
  buyUrl: string | null;
}

/** Respuesta 409 del API al crear o activar un usuario sin cupos libres. */
export const isSeatLimit = (error: unknown) => (error as any)?.response?.status === 409 && (error as any)?.response?.data?.code === 'SEAT_LIMIT';

/**
 * Aviso de cupos en Usuarios: en las empresas prepagadas muestra cuántos usuarios activos se usan de los pagados
 * y, si no quedan, el enlace al portal para comprar más. `version` cambia cuando se activa o crea un usuario.
 */
const SeatsNotice: React.FC<{ version?: number }> = ({ version = 0 }) => {
  const t = useT();
  const [state, setState] = useState<SeatState | null>(null);

  useEffect(() => {
    let alive = true;
    axios
      .get<SeatState>('users/seats')
      .then(({ data }) => alive && setState(data))
      .catch(() => alive && setState(null));
    return () => {
      alive = false;
    };
  }, [version]);

  if (!state?.limited || state.seats === null) return null;
  const full = (state.available ?? 0) <= 0;
  const buy = state.buyUrl ? (
    <Button size="small" variant={full ? 'contained' : 'outlined'} href={state.buyUrl} target="_blank" rel="noopener" endIcon={<OpenInNewIcon fontSize="small" />}>
      {t('users.seatsBuy')}
    </Button>
  ) : null;

  return (
    <Box sx={{ mb: 2 }}>
      <Notice severity={full ? 'warning' : 'info'} title={full ? t('users.seatsFullTitle') : undefined}>
        <Box sx={{ display: 'grid', gap: 1, minWidth: 0 }}>
          <span>{full ? t('users.seatsFull', { seats: state.seats }) : t('users.seatsUsed', { used: state.used, seats: state.seats })}</span>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.round((state.used / Math.max(1, state.seats)) * 100))}
            color={full ? 'warning' : 'primary'}
            aria-label={t('users.seatsUsed', { used: state.used, seats: state.seats })}
            sx={{ maxWidth: 360 }}
          />
          {buy && <Box>{buy}</Box>}
        </Box>
      </Notice>
    </Box>
  );
};

export default SeatsNotice;
